import type {ActionEnvelope, ChatState} from '@microsoft/agent-host-protocol'

import {AhpChatStore} from './ahp-chat-store'
import {type ChunkEnvelope, Reassembler} from './webpubsub/chunking'

const MAX_RESTART_EPOCH_INITIAL_SEQUENCE = 1

export class TaskEventReplayError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TaskEventReplayError'
  }
}

interface ReplayEvent {
  envelope: ActionEnvelope
  eventIndex: number
}

interface SessionReplayState {
  events: ReplayEvent[]
  modifiedAt: string
  nextSeq: number
  reassembler: Reassembler
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function requireNonEmptyString(value: unknown, field: string, eventIndex: number): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new TaskEventReplayError(`Task AHP event ${eventIndex} has an invalid ${field}.`)
  }
  return value
}

function requireNonNegativeInteger(value: unknown, field: string, eventIndex: number): number {
  if (!Number.isInteger(value) || (value as number) < 0) {
    throw new TaskEventReplayError(`Task AHP event ${eventIndex} has an invalid ${field}.`)
  }
  return value as number
}

function parseActionEnvelope(value: unknown, eventIndex: number): ActionEnvelope {
  if (!isRecord(value)) {
    throw new TaskEventReplayError(`Task AHP event ${eventIndex} did not contain an ActionEnvelope object.`)
  }

  requireNonEmptyString(value['channel'], 'payload.data.channel', eventIndex)
  requireNonNegativeInteger(value['serverSeq'], 'payload.data.serverSeq', eventIndex)

  const action = value['action']
  if (!isRecord(action)) {
    throw new TaskEventReplayError(`Task AHP event ${eventIndex} has an invalid payload.data.action.`)
  }
  requireNonEmptyString(action['type'], 'payload.data.action.type', eventIndex)

  const rejectionReason = value['rejectionReason']
  if (rejectionReason !== undefined && rejectionReason !== null) {
    requireNonEmptyString(rejectionReason, 'payload.data.rejectionReason', eventIndex)
  }

  return value as unknown as ActionEnvelope
}

function sessionChannelFor(sessionId: string): string {
  return sessionId.startsWith('ahp-session:/') ? sessionId : `ahp-session:/${sessionId}`
}

function createChatStateSeed(channel: string, modifiedAt: string): ChatState {
  return {
    resource: channel,
    title: '',
    status: 1,
    modifiedAt,
    turns: [],
  }
}

function changedDefaultChatChannel(envelope: ActionEnvelope, sessionChannel: string): string | undefined {
  if (envelope.channel !== sessionChannel || envelope.action.type !== 'session/defaultChatChanged') return undefined

  const defaultChat = (envelope.action as {defaultChat?: unknown}).defaultChat
  return typeof defaultChat === 'string' && defaultChat.length > 0 ? defaultChat : undefined
}

function canonicalChatChannel(sessionChannel: string, events: readonly ReplayEvent[]): string | undefined {
  let chatChannel: string | undefined

  for (const {envelope, eventIndex} of events) {
    if (envelope.action.type.startsWith('session/') && envelope.channel !== sessionChannel) {
      throw new TaskEventReplayError(
        `Task AHP event ${eventIndex} has action '${envelope.action.type}' on incompatible channel '${envelope.channel}'.`,
      )
    }
    chatChannel = changedDefaultChatChannel(envelope, sessionChannel) ?? chatChannel
    if (!chatChannel && envelope.action.type.startsWith('chat/')) {
      if (envelope.channel === sessionChannel) {
        throw new TaskEventReplayError(
          `Task AHP event ${eventIndex} has action '${envelope.action.type}' on incompatible channel '${envelope.channel}'.`,
        )
      }
      chatChannel = envelope.channel
    }
  }

  return chatChannel
}

function initialChatChannel(sessionChannel: string, events: readonly ReplayEvent[]): string | undefined {
  for (const {envelope} of events) {
    const advertised = changedDefaultChatChannel(envelope, sessionChannel)
    if (advertised) return advertised
    if (envelope.action.type.startsWith('chat/') && envelope.channel !== sessionChannel) return envelope.channel
  }
  return undefined
}

function validateCanonicalActionChannel(
  envelope: ActionEnvelope,
  eventIndex: number,
  sessionChannel: string,
  chatChannel: string,
): void {
  const actionType = envelope.action.type
  const hasCompatibleAction =
    (envelope.channel === sessionChannel && actionType.startsWith('session/')) ||
    (envelope.channel === chatChannel && actionType.startsWith('chat/'))
  if (!hasCompatibleAction) {
    throw new TaskEventReplayError(
      `Task AHP event ${eventIndex} has action '${actionType}' on incompatible channel '${envelope.channel}'.`,
    )
  }
}

function applyReplayEvent(
  store: AhpChatStore,
  replayEvent: ReplayEvent,
  sessionChannel: string,
  chatChannel: string,
): string {
  const {envelope, eventIndex} = replayEvent
  if (envelope.channel !== sessionChannel && envelope.channel !== chatChannel) return chatChannel

  validateCanonicalActionChannel(envelope, eventIndex, sessionChannel, chatChannel)
  try {
    store.applyAction(envelope)
  } catch (error) {
    const message = error instanceof Error ? error.message : 'unknown reducer failure'
    throw new TaskEventReplayError(`Task AHP event ${eventIndex} could not be applied: ${message}`)
  }
  return changedDefaultChatChannel(envelope, sessionChannel) ?? chatChannel
}

/**
 * Replays Mission Control's persisted AHP frames through the same
 * ActionEnvelope-to-AhpChatStore fold used by the live relay. Only the persisted
 * transport boundary is decoded here: event order, chunk integrity, the
 * single-session invariant, and the minimal ActionEnvelope shape.
 */
export function replayTaskAhpEvents(events: readonly unknown[]): ChatState | undefined {
  const sessions = new Map<string, SessionReplayState>()

  for (const [eventIndex, value] of events.entries()) {
    if (!isRecord(value)) {
      throw new TaskEventReplayError(`Task AHP event ${eventIndex} must be an object.`)
    }

    const sessionId = sessionChannelFor(requireNonEmptyString(value['session_id'], 'session_id', eventIndex))
    if (value['ns'] !== 'ahp') {
      throw new TaskEventReplayError(`Task AHP event ${eventIndex} has an invalid ns.`)
    }
    const seq = requireNonNegativeInteger(value['seq'], 'seq', eventIndex)
    const at = requireNonEmptyString(value['at'], 'at', eventIndex)

    let session = sessions.get(sessionId)
    if (!session) {
      session = {events: [], modifiedAt: at, nextSeq: seq, reassembler: new Reassembler()}
      sessions.set(sessionId, session)
    }
    session.modifiedAt = at
    const startsRestartEpoch =
      seq !== session.nextSeq && seq < session.nextSeq && seq <= MAX_RESTART_EPOCH_INITIAL_SEQUENCE
    if (seq !== session.nextSeq && !startsRestartEpoch) {
      throw new TaskEventReplayError(
        `Task AHP event ${eventIndex} for session '${sessionId}' has sequence ${seq}; expected ${session.nextSeq}.`,
      )
    }
    if (startsRestartEpoch) {
      // Mission Control can re-host a dormant session on a fresh mirror process.
      // Its transport sequence restarts at 0/1 while /events continues with only
      // the new actions, so preserve the prior fold but never carry a partial
      // chunk group across process lifetimes.
      session.nextSeq = seq
      session.reassembler = new Reassembler()
    }
    session.nextSeq += 1

    const payload = value['payload']
    const chunkGroupId =
      isRecord(payload) && payload['kind'] === 'chunk' && typeof payload['group_id'] === 'string'
        ? payload['group_id']
        : undefined
    let reassembled: unknown
    try {
      reassembled = session.reassembler.ingest(payload as ChunkEnvelope)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'unknown chunking failure'
      throw new TaskEventReplayError(
        `Task AHP event ${eventIndex} for session '${sessionId}' could not be reassembled: ${message}`,
      )
    }

    // Match live ingestion: an incomplete group has not emitted an action yet,
    // so replay keeps every completed action instead of failing the whole state.
    // Upstream (copilot-host session-mirror) sequences each ActionEnvelope's
    // chunks as a contiguous, gap-free, all-or-nothing run and never interleaves
    // groups, so an in-flight group here can only be the truncated tail of the
    // response; a torn mid-stream group instead surfaces as a sequence gap and
    // fails closed via the seq-continuity check above.
    if (reassembled === null && chunkGroupId && session.reassembler.hasInFlightGroup(chunkGroupId)) continue

    const envelope = parseActionEnvelope(reassembled, eventIndex)
    if (!envelope.rejectionReason) session.events.push({envelope, eventIndex})
  }

  if (sessions.size > 1) {
    throw new TaskEventReplayError(
      `Task AHP history contained multiple Mission Control sessions: ${[...sessions.keys()].join(', ')}.`,
    )
  }

  const sessionEntry = sessions.entries().next().value
  if (!sessionEntry) return undefined

  const [sessionChannel, session] = sessionEntry
  const chatChannel =
    canonicalChatChannel(sessionChannel, session.events) ??
    (session.events.length === 0 ? `${sessionChannel}/chat` : undefined)
  if (!chatChannel) return undefined
  const firstChatChannel = initialChatChannel(sessionChannel, session.events) ?? chatChannel
  const store = new AhpChatStore()
  store.start()
  store.applySnapshot(createChatStateSeed(chatChannel, session.modifiedAt))

  let activeChatChannel = firstChatChannel
  for (const replayEvent of session.events) {
    activeChatChannel = applyReplayEvent(store, replayEvent, sessionChannel, activeChatChannel)
  }

  const state = store.getSnapshot()
  if (!state) {
    throw new TaskEventReplayError(`Task AHP replay did not produce chat state for session '${sessionChannel}'.`)
  }
  return state
}
